import React, { useState, useCallback, useRef } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Slider, Typography, Box } from "@material-ui/core";
import ReactCrop from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { getCroppedImg } from "./canvasUtils";
import { makeStyles } from "@material-ui/core/styles";
import { toast } from "react-toastify";

const useStyles = makeStyles(theme => ({
	cropContainer: {
		position: "relative",
		width: "100%",
		height: 400,
		background: "#333",
	},
	controls: {
		padding: theme.spacing(2),
	},
	slider: {
		marginTop: theme.spacing(2),
	},
	previewContainer: {
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		marginTop: theme.spacing(2),
	},
	previewLabel: {
		marginBottom: theme.spacing(1),
		fontSize: "0.875rem",
		color: theme.palette.text.secondary,
	},
	previewAvatar: {
		width: 100,
		height: 100,
		borderRadius: "50%",
		overflow: "hidden",
		border: "2px solid",
		borderColor: theme.palette.primary.main,
		"& img": {
			width: "100%",
			height: "100%",
			objectFit: "cover",
		},
	},
}));

const ImageCropModal = ({ open, onClose, imageSrc, onCropComplete, aspect = 1, shape = "round" }) => {
	const classes = useStyles();
	const [crop, setCrop] = useState(null); // ✅ FIX 05/12/2025: Inicializar como null para evitar crop inválido
	const [croppedImage, setCroppedImage] = useState(null);
	const imgRef = useRef(null);
	const [scale, setScale] = useState(1);
	const [imageLoaded, setImageLoaded] = useState(false);

	const onImageLoad = useCallback((e) => {
		const img = e.currentTarget;
		const { naturalWidth, naturalHeight } = img;
		
		// ✅ Marcar imagen como cargada
		setImageLoaded(true);
		
		// ✅ Validar que las dimensiones sean válidas
		if (!naturalWidth || !naturalHeight || isNaN(naturalWidth) || isNaN(naturalHeight) || naturalWidth <= 0 || naturalHeight <= 0) {
			console.warn("Dimensiones de imagen inválidas:", { naturalWidth, naturalHeight });
			toast.error("Error: Dimensiones de imagen inválidas");
			return;
		}
		
		// ✅ FIX 05/12/2025: Calcular tamaño de recorte (30% del lado más pequeño para mejor UX)
		// Usar un tamaño más pequeño pero con un mínimo razonable para que sea manejable
		const minCropSize = 100; // Tamaño mínimo en píxeles
		const maxCropSize = Math.min(naturalWidth, naturalHeight) * 0.4; // Máximo 40% del lado más pequeño
		const cropSize = Math.max(minCropSize, Math.min(maxCropSize, Math.min(naturalWidth, naturalHeight) * 0.3));
		
		// ✅ Validar que cropSize sea válido
		if (isNaN(cropSize) || cropSize <= 0) {
			console.warn("Tamaño de recorte inválido:", cropSize);
			return;
		}
		
		// ✅ Calcular posición centrada perfectamente (asegurar que no sea negativo)
		const x = Math.max(0, Math.floor((naturalWidth - cropSize) / 2));
		const y = Math.max(0, Math.floor((naturalHeight - cropSize) / 2));
		
		// ✅ Validar posiciones
		if (isNaN(x) || isNaN(y) || x < 0 || y < 0) {
			console.warn("Posiciones de recorte inválidas:", { x, y });
			return;
		}
		
		// ✅ Actualizar crop con valores en píxeles
		setCrop({
			unit: "px",
			width: cropSize,
			height: cropSize,
			x,
			y,
		});
	}, []);

	const onCropChange = useCallback((crop) => {
		// ✅ Validar que el crop tenga valores válidos antes de actualizar
		// ✅ Permitir crops con unidades "%" o "px"
		if (crop) {
			// Si tiene width y height, validar que sean números válidos
			if (typeof crop.width !== 'undefined' && typeof crop.height !== 'undefined') {
				// Para unidades "%", los valores pueden ser porcentajes válidos
				if (crop.unit === "%") {
					if (!isNaN(crop.width) && !isNaN(crop.height) && crop.width > 0 && crop.height > 0) {
						setCrop(crop);
					}
				} 
				// Para unidades "px", validar que sean números positivos
				else if (crop.unit === "px") {
					if (!isNaN(crop.width) && !isNaN(crop.height) && crop.width > 0 && crop.height > 0) {
						setCrop(crop);
					}
				}
				// Si no tiene unit definido, permitir si los valores son válidos
				else if (!isNaN(crop.width) && !isNaN(crop.height) && crop.width > 0 && crop.height > 0) {
					setCrop(crop);
				}
			} else {
				// Si no tiene width/height, permitir el crop (puede ser un crop parcial)
				setCrop(crop);
			}
		}
	}, []);

	const makeClientCrop = useCallback(async (crop) => {
		if (imgRef.current && crop.width && crop.height) {
			const croppedImageUrl = await getCroppedImg(imgRef.current, crop, shape);
			setCroppedImage(croppedImageUrl);
		}
	}, [shape]);

	const handleCropComplete = useCallback((crop) => {
		makeClientCrop(crop);
	}, [makeClientCrop]);

	const handleSave = async () => {
		if (croppedImage) {
			onCropComplete(croppedImage);
		} else {
			// Si no hay imagen recortada, usar la original
			onCropComplete(imageSrc);
		}
		handleClose();
	};

	const handleClose = () => {
		setCrop({ unit: "%", width: 90, height: 90 });
		setScale(1);
		setCroppedImage(null);
		setImageLoaded(false);
		onClose();
	};

	return (
		<Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
			<DialogTitle>Recortar y ajustar imagen</DialogTitle>
			<DialogContent>
				<Box className={classes.cropContainer}>
					{imageSrc ? (
						<ReactCrop
							crop={crop} // ✅ FIX 05/12/2025: crop será null hasta que la imagen cargue
							onChange={onCropChange}
							onComplete={handleCropComplete}
							aspect={aspect}
							circularCrop={shape === "round"}
							disabled={!imageLoaded || !crop} // ✅ FIX 05/12/2025: Deshabilitar hasta que crop esté definido
						>
							<img
								ref={imgRef}
								alt="Crop me"
								src={imageSrc}
								style={{ 
									transform: `scale(${scale})`, 
									maxWidth: "100%",
									display: imageLoaded ? "block" : "none"
								}}
								onLoad={onImageLoad}
								onError={(e) => {
									console.error("Error cargando imagen:", e);
									// ✅ FIX 05/12/2025: Mensaje específico para errores de CSP
									if (e.target && e.target.src && e.target.src.startsWith('blob:')) {
										toast.error("El navegador bloqueó la carga de la imagen. Puede deberse a la política de seguridad (CSP) del servidor. Contacte a soporte si el problema persiste.");
									} else {
										toast.error("Error al cargar la imagen. Verifique que el archivo sea una imagen válida.");
									}
									setImageLoaded(false);
								}}
							/>
							{!imageLoaded && (
								<Box display="flex" justifyContent="center" alignItems="center" height="100%" position="absolute" width="100%">
									<Typography variant="body2" color="textSecondary">
										Cargando imagen...
									</Typography>
								</Box>
							)}
						</ReactCrop>
					) : (
						<Box display="flex" justifyContent="center" alignItems="center" height="100%">
							<Typography variant="body2" color="textSecondary">
								No hay imagen para recortar
							</Typography>
						</Box>
					)}
				</Box>
				<Box className={classes.controls}>
					<Typography variant="body2" gutterBottom>
						Zoom
					</Typography>
					<Slider
						className={classes.slider}
						value={scale}
						min={0.5}
						max={2}
						step={0.1}
						onChange={(e, value) => setScale(value)}
					/>
					<Box className={classes.previewContainer}>
						<Typography className={classes.previewLabel} variant="body2">
							Vista previa
						</Typography>
						<Box className={classes.previewAvatar}>
							{croppedImage ? (
								<img src={croppedImage} alt="Preview" />
							) : imageSrc ? (
								<img src={imageSrc} alt="Preview" style={{ objectFit: "cover" }} />
							) : null}
						</Box>
					</Box>
				</Box>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose} color="secondary">
					Cancelar
				</Button>
				<Button onClick={handleSave} color="primary" variant="contained">
					Guardar
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default ImageCropModal;

